import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils";
import { UploadCloud, CheckCircle2, AlertCircle, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * CS 21.73 — Design Catalog upload UI.
 *
 * Two-step: Preview (dedup counts, no writes) → Confirm (with policy).
 * Designs are style-keyed and isolated from the physical-stock upload path
 * — this page never triggers auto-return / bag dedup / cross-wholesaler
 * flows even when a design shares Style No or Jewel Code with stock.
 */
export default function UploadDesigns() {
  const [franchises, setFranchises] = useState([]);
  const [franchiseId, setFranchiseId] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [policy, setPolicy] = useState("skip");

  useEffect(() => {
    api.get("/franchises").then((r) => setFranchises(r.data || [])).catch(() => {});
  }, []);

  const handleFile = (f) => {
    setFile(f || null);
    setPreview(null);
    setResult(null);
  };

  const doPreview = async () => {
    if (!franchiseId) return toast.error("Pick a target franchise / catalog");
    if (!file) return toast.error("Choose an Excel file");
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/franchises/${franchiseId}/designs/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const doUpload = async () => {
    if (!franchiseId || !file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("policy", policy);
      const r = await api.post(`/franchises/${franchiseId}/designs/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`Designs uploaded — ${r.data.added} added, ${r.data.updated} updated`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const counts = preview?.counts || {};

  return (
    <div className="space-y-6" data-testid="design-upload-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Upload Designs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Style-based catalog upload. Designs are permanent catalog entries — physical stock
            uploads never touch them and vice versa.
          </p>
        </div>
        <Link to="/upload" className="text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-upload">
          ← Back to Product Upload
        </Link>
      </div>

      <div className="rounded-lg border bg-white p-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-sm font-medium text-slate-700">Target Catalog (Franchise)</label>
            <Select value={franchiseId} onValueChange={(v) => { setFranchiseId(v); setPreview(null); setResult(null); }}>
              <SelectTrigger data-testid="design-franchise-select" className="mt-1">
                <SelectValue placeholder="Choose a franchise…" />
              </SelectTrigger>
              <SelectContent>
                {franchises.filter((f) => f.is_active).map((f) => (
                  <SelectItem key={f.id} value={f.id} data-testid={`design-franchise-opt-${f.id}`}>
                    {f.name} <span className="text-slate-400">— {f.design_count || 0} designs</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Excel file</label>
            <Input
              type="file" accept=".xlsx"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="design-file-input"
              className="mt-1"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={doPreview} disabled={busy || !file || !franchiseId} data-testid="design-preview-btn">
            <UploadCloud className="w-4 h-4 mr-2" /> Preview
          </Button>
          {file && (
            <Button variant="ghost" size="sm" onClick={() => handleFile(null)} data-testid="design-clear-file">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border bg-white p-6 space-y-4" data-testid="design-preview-panel">
          <div className="grid grid-cols-4 gap-3">
            <Stat label="New" value={counts.new || 0} tone="emerald" testid="stat-new" />
            <Stat label="Duplicate (in catalog)" value={counts.dup_in_catalog || 0} tone="amber" testid="stat-dup-catalog" />
            <Stat label="Duplicate (in file)" value={counts.dup_in_file || 0} tone="amber" testid="stat-dup-file" />
            <Stat label="Errors" value={counts.errors || 0} tone="rose" testid="stat-errors" />
          </div>

          {(counts.dup_in_catalog || 0) > 0 && (
            <div className="text-sm text-slate-600">
              <label className="font-medium">On duplicates in this catalog:</label>
              <div className="mt-1 flex gap-3">
                <label className="flex items-center gap-2" data-testid="policy-skip">
                  <input type="radio" checked={policy === "skip"} onChange={() => setPolicy("skip")} />
                  Skip
                </label>
                <label className="flex items-center gap-2" data-testid="policy-overwrite">
                  <input type="radio" checked={policy === "overwrite"} onChange={() => setPolicy("overwrite")} />
                  Overwrite (this catalog only)
                </label>
              </div>
            </div>
          )}

          {preview.errors?.length > 0 && (
            <div className="rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              {preview.errors.map((e, i) => <div key={`err-${i}`}>{e}</div>)}
            </div>
          )}

          <Button onClick={doUpload} disabled={busy || (counts.new || 0) + (counts.dup_in_catalog || 0) === 0} data-testid="design-confirm-btn">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm upload
          </Button>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm" data-testid="design-result">
          <div className="font-medium text-emerald-900">Upload complete</div>
          <div className="text-emerald-800 mt-1">
            Added <b>{result.added}</b> · Updated <b>{result.updated}</b> · Skipped <b>{result.skipped}</b>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone, testid }) {
  const toneCls =
    tone === "emerald" ? "bg-emerald-50 text-emerald-900 border-emerald-200"
    : tone === "amber" ? "bg-amber-50 text-amber-900 border-amber-200"
    : tone === "rose" ? "bg-rose-50 text-rose-900 border-rose-200"
    : "bg-slate-50 text-slate-900 border-slate-200";
  return (
    <div className={`rounded border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs">{label}</div>
    </div>
  );
}
